import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Outlet, Navigate, useLocation } from 'react-router-dom';
import { Truck } from 'lucide-react';
import { Header } from './Header';
import { StatusBar } from './StatusBar';
import { RecordsSidebar } from './RecordsSidebar';
import { RecordsUnitList } from './RecordsUnitList';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { useSettingsContext } from '@/contexts/SettingsContext';
import { useFilters } from '@/contexts/FilterContext';
import { useUnitStatus } from '@/contexts/UnitStatusContext';
import { useIsMobile } from '@/hooks/useMediaQuery';
import { getUnits } from '@/lib/api';
import { cn } from '@/lib/utils';
import type { Unit, UnitFilterData } from '@/types';
import type { AlertFilterState } from '@/lib/unitFilters';
import { LottieLoading } from '@/components/ui/lottie-loading';

/**
 * Records Section Layout
 *
 * Internal-only section for fleet managers to manage:
 * - Unit inventory with attention scores
 * - Compliance/registration tracking
 * - Maintenance logs
 * - Work orders
 * - Check-in history
 *
 * Requires is_internal = true
 */
export function RecordsLayout() {
  const { user, loading: settingsLoading } = useSettingsContext();
  const {
    selectedCompanyIds,
    selectedGroupIds,
    selectedIndustryTypes,
    selectedModels,
    companies,
    groups,
    setSelectedCompanyIds,
    setSelectedGroupIds,
    setUnits: setContextUnits,
    units: contextUnits
  } = useFilters();
  const { getStatus } = useUnitStatus();
  const isMobile = useIsMobile();
  const location = useLocation();

  // Mobile unit picker: the desktop sidebar's unit list, in a bottom sheet, since
  // the sidebar is hidden on phones. Closes itself when the list navigates.
  const [unitPickerOpen, setUnitPickerOpen] = useState(false);
  useEffect(() => { setUnitPickerOpen(false); }, [location.pathname]);

  // State for StatusBar
  const [units, setUnits] = useState<(Unit | UnitFilterData)[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilters, setStatusFilters] = useState<Set<'online' | 'offline' | 'idle' | 'unknown'>>(
    new Set(['online', 'offline', 'idle', 'unknown'])
  );
  const [showAlerts, setShowAlerts] = useState<AlertFilterState>('none');
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);

  // Persist sidebar collapsed state in localStorage
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(() => {
    if (typeof window === 'undefined') return false;
    return localStorage.getItem('records_sidebar_collapsed') === 'true';
  });

  // Save sidebar state to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('records_sidebar_collapsed', String(isSidebarCollapsed));
  }, [isSidebarCollapsed]);

  // Track if we've already loaded to prevent double-loading
  const hasLoadedRef = useRef(false);

  // Load units for unit snapshot and push to FilterContext
  const loadUnits = useCallback(async (signal?: { cancelled: boolean }) => {
    try {
      const data = await getUnits();
      // Prevent state update if component unmounted
      if (signal?.cancelled) return;
      setUnits(data);
      setContextUnits(data);
    } catch (err) {
      // Prevent state update if component unmounted
      if (signal?.cancelled) return;
      console.error('Failed to load units for records:', err);
    }
  }, [setContextUnits]);

  useEffect(() => {
    // Track if component is still mounted
    const signal = { cancelled: false };

    if (hasLoadedRef.current) {
      if (contextUnits.length > 0) {
        setUnits(contextUnits);
      }
      return () => { signal.cancelled = true; };
    }

    if (contextUnits.length === 0) {
      hasLoadedRef.current = true;
      loadUnits(signal);
    } else {
      hasLoadedRef.current = true;
      setUnits(contextUnits);
    }

    return () => { signal.cancelled = true; };
  }, [contextUnits, loadUnits]);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await loadUnits();
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleClearFilters = () => {
    setSearchQuery('');
    setStatusFilters(new Set(['online', 'offline', 'idle', 'unknown']));
    setShowAlerts('none');
    setSelectedCompanyIds(companies.map(a => a.id));
    setSelectedGroupIds(groups.map(g => g.id));
  };

  // Calculate snapshot counts based on units filtered by Company/Group/Type/Model ONLY
  const snapshotUnits = useMemo(() => {
    return units.filter((unit: Unit | UnitFilterData) => {
      const unitType = unit.industry_type || 'film';
      const matchesType = selectedIndustryTypes.includes(unitType);

      const modelPrefix = unit.unit_code.substring(0, 3);
      const matchesModel = selectedModels.includes(modelPrefix);

      let matchesCompany = selectedCompanyIds.length === 0;
      if (!matchesCompany) {
        matchesCompany = selectedCompanyIds.includes(unit.company_id) ||
                        (selectedCompanyIds.includes('__unassigned__') && !unit.company_id);
      }

      let matchesGroup = selectedGroupIds.length === 0;
      if (!matchesGroup) {
        matchesGroup = (unit.primary_group_id && selectedGroupIds.includes(unit.primary_group_id)) ||
                      (selectedGroupIds.includes(`__unassigned_${unit.company_id}__`) && !unit.primary_group_id);
      }

      return matchesType && matchesModel && matchesCompany && matchesGroup;
    });
  }, [units, selectedIndustryTypes, selectedModels, selectedCompanyIds, selectedGroupIds]);

  // Calculate counts from the snapshot using live status
  const onlineCount = snapshotUnits.filter((t: Unit | UnitFilterData) => getStatus(t.id) === 'online').length;
  const offlineCount = snapshotUnits.filter((t: Unit | UnitFilterData) => getStatus(t.id) === 'offline').length;
  const idleCount = snapshotUnits.filter((t: Unit | UnitFilterData) => getStatus(t.id) === 'idle').length;
  const warningCount = snapshotUnits.filter((t: Unit | UnitFilterData) => t.alerts && t.alerts > 0 && t.alertType === 'warning').length;
  const faultCount = snapshotUnits.filter((t: Unit | UnitFilterData) => t.alerts && t.alerts > 0 && t.alertType === 'fault').length;

  if (settingsLoading) {
    return <LottieLoading message="Loading records..." />;
  }

  // Check if user is internal - Records is internal-only
  if (!user?.is_internal) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div className="h-dvh flex flex-col overflow-hidden bg-background">
      {/* Top Header & Status Bar - Fixed at top */}
      <div className="flex-none z-40">
        <Header />
        <StatusBar
          onlineCount={onlineCount}
          offlineCount={offlineCount}
          idleCount={idleCount}
          warningCount={warningCount}
          faultCount={faultCount}
          onClearFilters={handleClearFilters}
          onRefresh={handleRefresh}
          isRefreshing={isRefreshing}
          statusFilters={statusFilters}
          onStatusFiltersChange={setStatusFilters}
          showAlerts={showAlerts}
          onShowAlertsChange={setShowAlerts}
          units={units}
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          searchOpen={isSearchOpen}
          onSearchOpenChange={setIsSearchOpen}
        />
      </div>

      {/* Content Area with Sidebar */}
      <div className="flex flex-1 overflow-hidden relative">
        {/* Desktop: Show sidebar, Mobile: Hide (use hamburger menu instead) */}
        {!isMobile && (
          <RecordsSidebar
            collapsed={isSidebarCollapsed}
            onToggle={setIsSidebarCollapsed}
          />
        )}

        {/* Main Scrollable Content */}
        <main
          className={cn(
            "flex-1 overflow-y-auto transition-all duration-300 flex flex-col",
            isMobile ? "p-4" : "p-8",
            isMobile ? "ml-0" : (isSidebarCollapsed ? "ml-[60px]" : "ml-[240px] lg:ml-[360px]")
          )}
        >
          {isMobile && (
            <Button
              variant="outline"
              onClick={() => setUnitPickerOpen(true)}
              className="mb-4 w-full justify-start gap-2"
            >
              <Truck className="h-4 w-4" /> Browse units
            </Button>
          )}
          <Outlet />
        </main>

        {isMobile && (
          <Sheet open={unitPickerOpen} onOpenChange={setUnitPickerOpen}>
            <SheetContent side="bottom" className="flex h-[85vh] flex-col rounded-t-2xl p-0">
              <SheetHeader className="border-b border-border px-4 py-3 text-left">
                <SheetTitle>Units</SheetTitle>
              </SheetHeader>
              {/* RecordsUnitList is h-full with its own internal scroll — give it
                  height (min-h-0) rather than a second scroll container. */}
              <div className="min-h-0 flex-1">
                <RecordsUnitList collapsed={false} />
              </div>
            </SheetContent>
          </Sheet>
        )}
      </div>
    </div>
  );
}
